import { EMAIL_RE } from '../utils/valueFormats';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { redirect, Link as RouterLink, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
    Container, Typography, FormControl, FormGroup, FormControlLabel, Tooltip, Table, TableBody, TableCell,
    Checkbox, InputLabel, Select, MenuItem, RadioGroup, Radio, Button, Fade, TableContainer, TableHead, Snackbar,
    useMediaQuery, Grid, TextField, Autocomplete, Chip, CircularProgress, DialogContentText, TableRow,
    Backdrop, FormHelperText, Paper, Divider, List, ListItem, ListItemText, LinearProgress, Alert, InputAdornment,
    ListItemButton, ListItemIcon, Box, Dialog, DialogTitle, DialogContent, DialogActions, GlobalStyles
} from '@mui/material';
import Link from "@mui/material/Link";
import GoogleIcon from '@mui/icons-material/Google';
import firebase from 'firebase/compat/app';
import getIdToken from '../components/getIdToken';
import 'firebase/compat/auth';
import 'firebaseui/dist/firebaseui.css';
import { auth, db } from '../config/firebase';
import { getAuth, signInWithPopup, reload, GoogleAuthProvider, signOut, sendPasswordResetEmail, signInWithEmailAndPassword, fetchSignInMethodsForEmail } from 'firebase/auth';
import { useCredits } from '../contexts/CreditsContext';
import fetchData from '../utils/fetchData';
import TurnStile from '../components/TurnStile';
import logo_no_tagline from "../assets/logo_no_tagline.jpg";
import addUserToBackendIfFirstTime from '../utils/addUserToBackendIfFirstTime';
import addUserToFirestoreIfNotExists from '../utils/addUserToFirestoreIfNotExists';
import SignInWithGoogle from '../components/SignInWithGoogle';
import updateUserInFirestore from '../utils/updateUserInFirestore';
import {
    saveRedirectUrl,
    handlePostAuthRedirect,
    initializeRedirect,
    getRedirectParameter
} from '../components/redirectHandler';
import { notifySeverity } from '../utils/snackbarSeverity';

const Home = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState({ email: '', password: '' });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarTimeout, setSnackbarTimeout] = useState(null);
    const { refreshCredits } = useCredits();
    const [widgetPassed, setWidgetPassed] = useState(false);
    // Start true so the first render shows the auth-check loader instead of
    // flashing the sign-in form for the brief window before Firebase's
    // onAuthStateChanged fires. Set false in the else branch (no user) below.
    const [checkingUser, setCheckingUser] = useState(true);
    const [emailFocused, setEmailFocused] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [isLogin, setIsLogin] = useState(false);
    const [isEmailLogin, setIsEmailLogin] = useState(false);
    const tokenRef = useRef(null);
    const [tokenValue, setTokenValue] = useState(null);
    const turnstileRef = useRef(null);
    const humanCheckRef = useRef(null); // the Turnstile box, for SignInWithGoogle to scroll to
    const hasRedirectedRef = useRef(false);

    // Stable callbacks for <TurnStile>. Its effect re-renders the widget whenever
    // these change, and inline arrows changed on every render of this page, so any
    // state update (a snackbar, a keystroke) removed and re-created the widget —
    // wiping a "Verify you are human" checkbox the user was asked to tick.
    const onTurnstileToken = useCallback((token) => { tokenRef.current = token; setTokenValue(token); setWidgetPassed(!!token); }, []);
    const onTurnstileError = useCallback(({ message }) => {
        tokenRef.current = null;
        setTokenValue(null);
        setWidgetPassed(false);
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    }, []);
    const isManualLoginRef = useRef(false);
    /*
     * NO PRE-SIGN-IN LOOKUP. This page used to query the Firestore `users`
     * collection by the typed email on every keystroke — before sign-in — to
     * tailor the error ("you registered with Google"). That only worked because
     * the database was readable by anyone, and it handed whoever was typing the
     * whole user document (admin flag, Google settings). With the rules locked
     * down it can't run, and it shouldn't: one honest message covers both cases
     * and says nothing about whether an email has an account.
     */
    const [connectionId, setConnectionId] = useState('');
    const [shop, setShop] = useState('');

    // Handle redirect parameter on component mount
    useEffect(() => {
        initializeRedirect(searchParams);
    }, [searchParams]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            setCheckingUser(true);

            try {
                if (user !== null && !isLogin && !isManualLoginRef.current) {
                    // Silent re-auth path: Firebase already has a cached token
                    // for this user — that IS the proof of identity. Turnstile
                    // gates the *manual sign-in* form (see signInWithEmail
                    // which waits on tokenValue / waitForToken), not the
                    // silent reload path. Previously we returned here when
                    // widgetPassed was still false, which flipped the loader
                    // off and exposed the sign-in form just long enough for
                    // Turnstile to mount inside it — causing a visible
                    // form-flash → loader → loader-text-change cascade on
                    // every refresh. Now we proceed straight to reload +
                    // validate + redirect, with the loader staying up the
                    // whole way.

                    await reload(user);

                    const emailProvider = user.providerData.some(
                        (provider) => provider.providerId === "password"
                    );

                    if (emailProvider && !user.emailVerified) {
                        setIsLoggedIn(false);
                        setCheckingUser(false);
                        setSnackbarMessage("Please verify your email before logging in.");
                        setSnackbarOpen(true);
                        return;
                    }

                    // Only proceed if user is allowed
                    await addUserToBackendIfFirstTime(user.uid, user.email, refreshCredits);

                    // CHECK USER STATUS FIRST
                    const isAllowed = await checkUserStatus(user.uid);
                    if (!isAllowed) {
                        setCheckingUser(false);
                        // DO NOT REDIRECT - Let the user see the snackbar message
                        return;
                    }

                    // A pending Shopify connection is no longer attached here: it
                    // blocked sign-in when it failed and attached silently when it
                    // didn't. ShopifyConnectPrompt asks once the user is inside.

                    setCheckingUser(false);
                    setIsLoggedIn(true);

                    // Only redirect if we're still on the login page AND user is allowed
                    if (location.pathname === '/' || location.pathname === '') {
                        handlePostAuthRedirect(navigate, true);
                    }
                } else {
                    setCheckingUser(false);
                    if (isLoggedIn) {
                        navigate("/");
                        setIsLoggedIn(false);
                    }
                }
            } catch (error) {
                setCheckingUser(false);
                console.error("Auth state error:", error);
            }
        });

        return () => unsubscribe();
        // widgetPassed intentionally NOT in deps — silent re-auth no longer
        // depends on Turnstile; re-running on every Turnstile state change
        // just re-wires the auth listener for no reason.
    }, [navigate, isLoggedIn, location.pathname]);

    useEffect(() => {
        if (location.state?.snackbarMessage) {
            setSnackbarMessage(location.state.snackbarMessage);
            setSnackbarOpen(true);
            localStorage.setItem('snackbarMessage', location.state.snackbarMessage);
        }
    }, [location.state]);

    useEffect(() => {
        const storedMessage = localStorage.getItem('snackbarMessage');
        if (storedMessage) {
            setSnackbarMessage(storedMessage);
            setSnackbarOpen(true);
        }
    }, []);

    /*
     * The app's rule, not a looser local one. This was `/^\S+@\S+\.\S+$/`, and
     * `\S` matches an @ — so "a@b@c.d" was accepted at sign-up and refused by
     * every screen afterwards.
     */
    const validateEmail = (email) => EMAIL_RE.test(String(email ?? '').trim());

    const checkUserStatus = async (uid) => {
        try {
            const apiUrl = `${process.env.REACT_APP_API_BASE_URL}ai/writer/v1/user/firebase/${uid}`;
            const response = await fetchData({
                url: apiUrl,
            });

            if (!response?.error) {
                // User found, check if isBlocked is true
                if (response.user?.isBlocked) {
                    setSnackbarMessage(
                        "Your account is temporarily disabled. Please contact customer support!"
                    );
                    setSnackbarOpen(true);
                    setIsLoggedIn(false);

                    // Sign the user out
                    const auth = getAuth();
                    await signOut(auth);
                    return false; // Block login
                }
            } else {
                // User not found in backend
                setSnackbarMessage(
                    "Your account cannot be found. Please contact customer support!"
                );
                setSnackbarOpen(true);
                setIsLoggedIn(false);
                return false;
            }

            return true;
        } catch (error) {
            console.error("Error checking user status:", error);

            const msg = error.message || "";

            if (msg.includes("NetworkError") || msg.includes("ERR_CONNECTION_REFUSED")) {
                setSnackbarMessage(
                    "Failed to connect. You might be blocked by security verification."
                );
            } else {
                setSnackbarMessage(
                    "Failed checking user status. Please try again later."
                );
            }

            setSnackbarOpen(true);
            setIsLoggedIn(false);
            return false;
        }
    };

    const waitForToken = (timeoutMs = 3000, intervalMs = 500) => {
        return new Promise((resolve) => {
            const start = Date.now();

            const check = () => {
                if (tokenRef.current) {
                    resolve(tokenRef.current);
                } else if (Date.now() - start >= timeoutMs) {
                    resolve(null);
                } else {
                    setTimeout(check, intervalMs);
                }
            };

            check();
        })
    }


    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            const newTokenValue = await waitForToken(10000, 500);
            if (!cancelled) {
                setTokenValue(newTokenValue);
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, []);

    const signInWithEmail = async (event) => {
        event.preventDefault();
        setIsLogin(true);
        setIsEmailLogin(true);

        const newErrors = { email: '', password: '' };
        if (!email) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(email)) {
            newErrors.email = 'Invalid email format';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        }

        if (newErrors.email || newErrors.password) {
            setLoginError(newErrors);
            setIsLogin(false);
            setIsEmailLogin(false);
            return;
        }

        if (tokenValue == null) {
            setSnackbarMessage("Waiting to verify you are human.");
            setSnackbarOpen(true);
            setIsLogin(false);
            setIsEmailLogin(false);
            return;
        }


        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}xeo/botVerification`;

        // 带 5 秒超时的 fetch
        const fetchWithTimeout = (url, options, timeout = 5000) => {
            return Promise.race([
                fetch(url, options),
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error("timeout")), timeout)
                )
            ]);
        };

        let resData;
        try {
            const response = await fetchWithTimeout(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ Token: tokenValue }),
            }, 7000); // 7 秒超时

            resData = await response.json();

            if (!resData?.success) {
                setSnackbarMessage("Verification failed. Please try again.");
                setSnackbarOpen(true);
                setIsLogin(false);
                setIsEmailLogin(false);
                return;
            }
        } catch (error) {
            setSnackbarMessage("Something wrong with the verification. Please try again.");
            setSnackbarOpen(true);
            setIsLogin(false);
            setIsEmailLogin(false);
            return;
        }

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            await user.reload();

            if (!user.emailVerified) {
                setSnackbarMessage("Please verify your email before logging in.");
                setSnackbarOpen(true);
                setIsLogin(false);
                setIsEmailLogin(false);
                return;
            }

            // Add user to Firestore users collection if not exists
            const firestoreRes = await addUserToFirestoreIfNotExists(user.uid, user.email, user.providerData);
            const existed = firestoreRes.existed;
            if (existed) {
                await updateUserInFirestore(user.uid, user.providerData);
            }
        } catch (error) {
            turnstileRef.current?.resetToken();
            setWidgetPassed(false);

            let message = "Failed to login, try again later.";

            if (
                error.code?.includes("invalid-login-credentials") ||
                error.code === "auth/invalid-credential" ||
                error.code === "auth/user-not-found" ||
                error.code === "auth/wrong-password"
            ) {
                message = "Invalid email or password. If you signed up with Google, use Sign in with Google instead.";
            } else if (error.code === "auth/too-many-requests") {
                message = "Tried too many times, please try again later.";
            }

            setSnackbarMessage(message);
            setSnackbarOpen(true);
        } finally {
            setIsLogin(false);
            setIsEmailLogin(false);
            setPassword('');
        }
    }

    const showLoader = isLoggedIn || checkingUser;
    return (
        <Box sx={{
            // Fill the viewport exactly with position:fixed + inset:0 — the
            // page CAN'T grow beyond the viewport, so no scrollbar can ever
            // appear (body stays at the natural document size). Content is
            // pushed from the top (not vertically centered) so the card sits
            // slightly above the middle.
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            // Scrolls INSIDE the frame. It was overflow:hidden, so whatever didn't
            // fit — a phone in landscape, or the form with the keyboard up — was
            // cut off with no way to reach it. Scrollbars are hidden app-wide, so
            // on a screen where it fits nothing changes.
            overflowX: 'hidden',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            backgroundColor: 'var(--surface)',
            // Blue glow sits BEHIND THE CARD (not the logo) so the logo's PNG
            // white envelope sits on pure white and disappears via the
            // mix-blend-mode below. Corner accents add ambience without
            // crossing the logo area.
            backgroundImage:
                'radial-gradient(ellipse 55% 38% at 50% 60%, rgba(25,118,210,0.12), transparent 70%),' +
                'radial-gradient(circle at 100% 0%, rgba(25,118,210,0.06), transparent 35%),' +
                'radial-gradient(circle at 0% 100%, rgba(13,71,161,0.05), transparent 40%)',
            px: { xs: 2.5, sm: 3 },
            pt: { xs: '5vh', sm: '8vh' },
            pb: 2,
        }}>
            {showLoader ? (
                /* Auth-check loader — same outer column as the Sign In branch:
                   masked logo above (xs 110 / sm 130, identical to Sign In /
                   Sign Up / Reset), card below (4px brand-blue gradient stripe,
                   soft shadow). When Firebase resolves and we flip into the
                   form or out to the dashboard, the logo doesn't jump position
                   or size — the card is the only thing that swaps under it. */
                <Fade in timeout={400}>
                <Box sx={{
                    width: '100%',
                    maxWidth: 440,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    position: 'relative',
                    zIndex: 1,
                }}>
                    {/* Masked logo — outside the card, same size as the Sign In
                        page, so the eye registers it as a stable anchor between
                        loader → form / dashboard transitions. */}
                    <Box component="img" src={logo_no_tagline} alt="XEO"
                        sx={{
                            width: { xs: 110, sm: 130 },
                            height: 'auto',
                            mixBlendMode: 'multiply',
                            WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 55%, transparent 100%)',
                            maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 55%, transparent 100%)',
                        }} />

                    {/* Loader card — matches the form card's dimensions and
                        skin (full width of the 440 column, 4px gradient
                        stripe, soft shadow). */}
                    <Box sx={{
                        width: '100%',
                        backgroundColor: 'var(--surface)',
                        px: { xs: 3.5, sm: 4 },
                        pt: { xs: 4, sm: 4.5 },
                        pb: { xs: 4.5, sm: 5 },
                        borderRadius: 3,
                        border: '1px solid var(--rule)',
                        boxShadow: '0 20px 60px rgba(15,31,61,0.10), 0 4px 16px rgba(25,118,210,0.06)',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2.25,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0, left: 0, right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                        },
                    }}>
                        {/* Dual-ring spinner: pale full-circle track + active arc
                            in brand-blue with rounded line caps, sitting inside a
                            soft radial blue halo for depth. */}
                        <Box sx={{
                            position: 'relative',
                            width: 64,
                            height: 64,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Box sx={{
                                position: 'absolute',
                                inset: -4,
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(25,118,210,0.12) 0%, transparent 65%)',
                            }} />
                            <CircularProgress
                                variant="determinate"
                                value={100}
                                size={56}
                                thickness={3.5}
                                sx={{ color: 'var(--line)', position: 'absolute' }}
                            />
                            <CircularProgress
                                size={56}
                                thickness={3.5}
                                sx={{
                                    color: 'var(--accent)',
                                    position: 'absolute',
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round',
                                    },
                                }}
                            />
                        </Box>

                        {/* Two-line copy — primary is the stable headline,
                            secondary clarifies what's actually happening
                            without sliding into "Taking you to your dashboard"
                            territory mid-flow. */}
                        <Box sx={{ textAlign: 'center', mt: 0.25 }}>
                            <Typography sx={{
                                fontSize: '1.05rem',
                                fontWeight: 700,
                                color: 'var(--ink)',
                                letterSpacing: '-0.01em',
                                mb: 0.5,
                            }}>
                                Just a moment
                            </Typography>
                            <Typography sx={{
                                fontSize: '0.82rem',
                                color: 'var(--ink-3)',
                            }}>
                                Securely signing you in
                            </Typography>
                        </Box>

                        {/* Indeterminate progress bar pinned to the card's
                            bottom edge — gives the eye a continuous sense of
                            activity even while the CircularProgress is mid-
                            rotation. */}
                        <LinearProgress sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            backgroundColor: 'var(--surface-2)',
                            '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #1976d2, #0d47a1)',
                            },
                        }} />
                    </Box>
                </Box>
                </Fade>
            ) : (
                <Fade in timeout={500}>
                <Box sx={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
                    {/* Logo — original asset. The PNG has a hard white envelope
                        which used to show as a rectangle against the page bg.
                        We fade those edges to transparent with a radial CSS
                        mask, so only the dark mark in the centre stays opaque
                        and the surrounding white fades into the page. Also keep
                        mixBlendMode: multiply as a belt-and-braces — on a pure-
                        white bg it's a no-op; on any tint, it helps. */}
                    <Box component="img" src={logo_no_tagline} alt="XEO"
                        sx={{
                            width: { xs: 110, sm: 130 },
                            height: 'auto',
                            mixBlendMode: 'multiply',
                            WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 55%, transparent 100%)',
                            maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 55%, transparent 100%)',
                        }} />

                    {/* Form card — clean white with a brand-blue gradient stripe
                        at the top edge so it picks up the navbar palette without
                        a heavy coloured pane. */}
                    <Box sx={{
                        width: '100%',
                        backgroundColor: 'var(--surface)',
                        px: { xs: 3, sm: 4.5 },
                        py: { xs: 3, sm: 3.5 },
                        borderRadius: 3,
                        border: '1px solid var(--rule)',
                        boxShadow: '0 20px 60px rgba(15,31,61,0.10), 0 4px 16px rgba(25,118,210,0.06)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0, left: 0, right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                        },
                    }}>
                        <Typography sx={{ fontSize: { xs: '1.17rem', sm: '1.5rem' }, fontWeight: 800, color: 'var(--ink)', textAlign: 'center', mb: 0.5, letterSpacing: '-0.015em' }}>
                            Welcome back
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: 'var(--ink-3)', textAlign: 'center', mb: 2.5 }}>
                            Sign in to continue to XEO
                        </Typography>

                        <SignInWithGoogle
                            checkUserStatus={checkUserStatus}
                            setSnackbarMessage={setSnackbarMessage}
                            setSnackbarOpen={setSnackbarOpen}
                            setIsLogin={setIsLogin}
                            tokenValue={tokenValue}
                            label="Sign in with Google"
                            refreshCredits={refreshCredits}
                            isManualLoginRef={isManualLoginRef}
                            humanCheckRef={humanCheckRef}
                        />

                        <Divider sx={{ my: 2.25, color: 'var(--ink-4)', fontSize: '0.78rem', '&::before, &::after': { borderColor: 'var(--line)' } }}>or</Divider>

                        <Box
                            component="form"
                            noValidate
                            onSubmit={signInWithEmail}
                            sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, width: '100%' }}
                        >
                            {/* autoComplete pairs these two for a phone's password
                                manager, so it fills and saves them as one login. */}
                            <TextField
                                label="Email"
                                name="email"
                                type="email"
                                autoComplete="username"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); setLoginError({ email: '', password: '' }); }}
                                onFocus={() => setEmailFocused(true)}
                                onBlur={() => setEmailFocused(false)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                error={!!loginError.email}
                                helperText={loginError.email || ''}
                            />
                            <TextField
                                label="Password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); setLoginError({ email: '', password: '' }); }}
                                onFocus={() => setPasswordFocused(true)}
                                onBlur={() => setPasswordFocused(false)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                error={!!loginError.password}
                                helperText={loginError.password || ''}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.95rem',
                                    borderRadius: 2,
                                    py: 1.35,
                                    mt: 0.75,
                                    boxShadow: '0 4px 14px rgba(25,118,210,0.22)',
                                    '&:hover': { boxShadow: '0 6px 20px rgba(25,118,210,0.32)' },
                                }}
                            >
                                {isEmailLogin ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Sign In'}
                            </Button>
                        </Box>

                        {/* Cloudflare Turnstile — INSIDE the card. The widget
                            itself shows Cloudflare branding when visible, so we
                            don't add any caption (which would only duplicate
                            what Cloudflare already shows on its own chip). */}
                        <Box ref={humanCheckRef} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <TurnStile
                                ref={turnstileRef}
                                setToken={onTurnstileToken}
                                onError={onTurnstileError}
                            />
                        </Box>

                        <Box sx={{ textAlign: 'center', mt: 2.25, fontSize: '0.82rem', color: 'var(--ink-3)', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                            <Link component={RouterLink} to="/signup" underline="hover" sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', minHeight: 32 }}>Sign Up</Link>
                            <Box component="span" sx={{ color: 'var(--ink-4)' }}>·</Box>
                            <Link component={RouterLink} to="/forget-password" underline="hover" sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', minHeight: 32 }}>Reset Password</Link>
                            <Box component="span" sx={{ color: 'var(--ink-4)' }}>·</Box>
                            <Link href="https://xeoai.com/en/support" underline="hover" sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', minHeight: 32 }}>Contact Support</Link>
                        </Box>
                    </Box>
                </Box>
                </Fade>
            )}

            <Snackbar
                open={snackbarOpen}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ mt: 5, mr: 1 }}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={notifySeverity(snackbarMessage)} sx={{ width: '100%', fontSize: '1.0rem' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Home;
